import { IButton } from '@nocode/types';
import { get, pick } from 'lodash';
import queryString from 'query-string';
import React, { ComponentProps } from 'react';
import { Platform, Text } from 'react-native';
import { Button as RNButton } from 'react-native-paper';
import PointIcon from '../CustomIcon';
import { getActions, getItemListClick, getValueBinding } from '../../shared';
import { checkFont } from '../func';
import { processStyles, mergeStyles } from '../util.style';
import ButtonConfig from './config';
import { isCanvas } from '@common/utils';

type Props = ComponentProps<typeof RNButton> | any;

const Button: React.FC<IButton> = (props) => {
  const {
    attributes,
    onPress,
    record = null,
    data,
    isInCustomList,
    loading,
    style,
    libraryComponentManifest,
    ...otherProps
  } = props;

  const search = queryString.parse(window?.location?.search);
  const target = search?.target;

  const bindingValue = getValueBinding(
    isInCustomList ? props.initId : props.id,
    isInCustomList ? props?.record : data,
    props
  );

  if (isCanvas()) {
    // use x and y for canvas position
    delete style?.left;
    delete style?.top;
    delete style?.right;
    delete style?.bottom;
  }

  const { finalStyle, componentProps } = processStyles({
    attributes,
    style,
    config: ButtonConfig,
    ...otherProps,
  });

  const {
    type = 'contained',
    icon,
    upperCase,
    text: attributeText,
  } = componentProps;

  const textOptions = get(bindingValue, 'text', attributeText || '').toString();

  const formattedText = textOptions
    ? upperCase
      ? textOptions.toUpperCase()
      : textOptions.charAt(0).toUpperCase() + textOptions.slice(1)
    : '';

  const labelStyle = {
    fontSize: finalStyle.fontSize,
    fontFamily: checkFont(finalStyle.fontFamily, undefined, target),
    fontWeight: finalStyle.fontWeight?.toString(),
    color: finalStyle.color,
    fontStyle: finalStyle.fontStyle,
    textAlign: finalStyle.textAlign,
    textDecorationLine:
      finalStyle.textDecorationLine || finalStyle.textDecoration,
    textDecorationStyle: finalStyle.textDecorationStyle,
    textDecorationColor: finalStyle.textDecorationColor,
    textTransform: finalStyle.textTransform,
    letterSpacing: finalStyle.letterSpacing,
    lineHeight:
      finalStyle.lineHeight ||
      (finalStyle.fontSize ? finalStyle.fontSize * 1.2 : undefined),
    overflow: 'visible' as const,
  };
  const nestedTextStyles = attributes?.styles?.text || {};
  const mergedLabelStyle = mergeStyles(nestedTextStyles, labelStyle);

  // Calculate effective lineHeight for height calculations
  const effectiveLineHeight =
    finalStyle.lineHeight ||
    (finalStyle.fontSize ? finalStyle.fontSize * 1.2 : undefined);

  // Conditional styling based on button type
  const getContainerStyleByType = () => {
    const fontBasedHeight = finalStyle.fontSize ? finalStyle.fontSize + 30 : 44;
    const lineHeightBasedHeight = effectiveLineHeight
      ? effectiveLineHeight + 20
      : 0;
    const dynamicMinHeight = Math.max(fontBasedHeight, lineHeightBasedHeight);

    const baseStyle = {
      opacity: finalStyle.opacity,
      minHeight: dynamicMinHeight,
      height: style?.height,
      width: style?.width,
      overflow: 'visible' as const,
      ...(style.position === 'relative'
        ? pick(style, ['position'])
        : pick(style, [
            'position',
            'top',
            'left',
            'right',
            'bottom',
            'zIndex',
            'transform',
          ])),
    };

    switch (type) {
      case 'contained':
        return {
          ...baseStyle,
          backgroundColor: finalStyle.backgroundColor,
          borderRadius: finalStyle.borderRadius,
          borderWidth: finalStyle.borderWidth,
          borderColor: finalStyle.borderColor || finalStyle.backgroundColor,
          borderStyle: finalStyle.borderStyle,
          elevation: finalStyle.elevation,
          shadowColor: finalStyle.shadowColor,
          shadowOffset: finalStyle.shadowOffset,
          shadowOpacity: finalStyle.shadowOpacity,
          shadowRadius: finalStyle.shadowRadius,
        };

      case 'outlined':
        // Border and text styling only
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          borderRadius: finalStyle.borderRadius,
          borderWidth: finalStyle.borderWidth || 1,
          borderColor: finalStyle.borderColor || finalStyle.color,
          borderStyle: finalStyle.borderStyle || 'solid',
        };

      case 'text':
        // Text styling only (no background, no borders)
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          borderWidth: 0,
        };

      default:
        return {
          ...baseStyle,
          backgroundColor: finalStyle.backgroundColor,
          borderRadius: finalStyle.borderRadius,
          borderColor: finalStyle.backgroundColor,
        };
    }
  };

  const containerStyle = getContainerStyleByType();

  const contentStyle = {
    // Allow content to expand vertically, considering lineHeight
    minHeight: (() => {
      const fontBasedHeight = finalStyle.fontSize
        ? finalStyle.fontSize + 20
        : 44;
      const lineHeightBasedHeight = effectiveLineHeight
        ? effectiveLineHeight + 10
        : 0;
      return Math.max(fontBasedHeight, lineHeightBasedHeight);
    })(),
    justifyContent:
      finalStyle.textAlign === 'left'
        ? ('flex-start' as const)
        : finalStyle.textAlign === 'right'
        ? ('flex-end' as const)
        : finalStyle.textAlign === 'center'
        ? ('center' as const)
        : ('center' as const),
    alignItems: 'center' as const,
    height: style?.height,
    width: '100%',
  };
  const handlePress = () => {
    if (loading) {
      return;
    }

    onPress &&
      onPress(getActions(props, 'action'), {
        itemListClick: getItemListClick(record),
      });
  };

  if (icon === 'point') {
    return (
      <RNButton
        mode={type}
        style={containerStyle}
        contentStyle={contentStyle}
        uppercase={upperCase || false}
        labelStyle={{ fontSize: finalStyle.fontSize, color: finalStyle.color }}
        onPress={handlePress}
        icon={() => (
          <PointIcon
            width={24}
            height={24}
            fill={mergedLabelStyle.color || '#fff'}
          />
        )}
      >
        <Text numberOfLines={1} ellipsizeMode="tail" style={mergedLabelStyle}>
          {formattedText}
        </Text>
      </RNButton>
    );
  }

  return (
    <RNButton
      mode={type}
      style={containerStyle}
      contentStyle={contentStyle}
      uppercase={upperCase || false}
      icon={icon}
      labelStyle={{ fontSize: finalStyle.fontSize, color: finalStyle.color }}
      onPress={handlePress}
    >
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        style={{
          ...mergedLabelStyle,
          lineHeight:
            mergedLabelStyle.lineHeight &&
            mergedLabelStyle.lineHeight < mergedLabelStyle.fontSize
              ? mergedLabelStyle.fontSize * 1.2
              : mergedLabelStyle.lineHeight,
          overflow: 'hidden',
        }}
      >
        {formattedText}
      </Text>
    </RNButton>
  );
};

export default Button;
